import ReloadIcon from "@mui/icons-material/Replay";
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Pagination,
  TextField,
  Typography,
  Skeleton
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchGenres } from "../../app/slices/commonSlice";
import { fetchUploads, resetStateUploads } from "../../app/slices/uploadSlice";
import { translate } from "../../app/utils";
import BookCard from "./bookCard";

const Books = () => {
  const dispatch = useDispatch();
  const { state } = useLocation();

  const { list: books, status } = useSelector(({ uploads }) => uploads.get);

  const { list: genreList } = useSelector(({ common }) => common.genres);

  const [genres, setGenres] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [formValues, setFormValues] = useState({
    query: state?.query || "",
    author: "",
    genre: state?.genre || "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    dispatch(fetchUploads({ TYPE: "eBook", STATUS: "A" }));
    dispatch(fetchGenres({ resource: "genres" }));

    return () => {
      dispatch(resetStateUploads(["get"]));
    };
  }, [dispatch]);

  useEffect(() => {
    if (books?.length) {
      setFilteredBooks(books);
    }
  }, [books]);

  useEffect(() => {
    if (genreList?.length) {
      setGenres([...new Set(genreList)]);
    }
  }, [genreList]);

  useEffect(() => {
    if (!books?.length) return;

    const result = books.filter((book) => {
      const queryMatch = formValues.query
        ? Object.values(book).some((value) =>
            String(value)
              .toLowerCase()
              .includes(formValues.query.toLowerCase()),
          )
        : true;

      const authorMatch = formValues.author
        ? book.AUTHOR_NAME?.toLowerCase().includes(
            formValues.author.toLowerCase(),
          )
        : true;

      const genreMatch = formValues.genre
        ? String(book.GENRE_ID) === String(formValues.genre)
        : true;

      return queryMatch && authorMatch && genreMatch;
    });

    setFilteredBooks(result);
    setCurrentPage(1);
  }, [books, formValues]);

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
  };

  const currentBooks = filteredBooks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const reloadGenres = () => {
    dispatch(fetchGenres({ resource: "genres" }));
  };

  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {translate("menuBookCollection")}
      </Typography>

      {/* FILTERS */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            size="small"
            label={translate("search")}
            name="query"
            value={formValues.query}
            onChange={handleChange}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Autocomplete
            options={genres.sort((a, b) => a.TITLE.localeCompare(b.TITLE))}
            getOptionLabel={(option) => option.TITLE}
            value={genres.find((r) => r.GENRE_ID === formValues.genre) || null}
            onChange={(_, value) =>
              setFormValues((prev) => ({
                ...prev,
                genre: value?.GENRE_ID || "",
              }))
            }
            noOptionsText={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">No options</Typography>
                <IconButton size="small" onClick={reloadGenres}>
                  <ReloadIcon fontSize="small" />
                </IconButton>
              </Box>
            }
            renderInput={(params) => (
              <TextField {...params} label={translate("genre")} size="small" />
            )}
          />
        </Grid>
      </Grid>

      {/* BOOK GRID */}
      {status === "loading" ? (
        <Grid container spacing={3}>
          {Array.from(new Array(itemsPerPage)).map((_, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Skeleton variant="rectangular" width="100%" height={260} animation="wave" />
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="text" width="90%" height={28} animation="wave" />
                  <Skeleton variant="text" width="60%" height={20} animation="wave" sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="40%" height={16} animation="wave" sx={{ mt: 1 }} />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <BookCard books={currentBooks} />
      )}

      {/* PAGINATION */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Pagination
          count={Math.ceil(filteredBooks.length / itemsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default Books;
